import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Pagination from '@mui/material/Pagination';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import useDebounce from '../../resources/hooks/UseDebounce';
import {
  ApiError,
  createCustomer,
  exportCustomersFile,
  fetchCustomerDetail,
  fetchCustomerListPage,
  fetchCustomerLookups,
  patchCustomerStatus,
  updateCustomer,
} from './customerCenterApi';
import CustomerDepositsModal from './CustomerDepositsModal';
import CustomerFormModal from './CustomerFormModal';
import ImportCustomersModal from './ImportCustomersModal';
import RowMenuToggle from './RowMenuToggle';
import { EMPTY_CUSTOMER_DETAIL, mapServerFieldErrors } from './customerFormUtils';
import type { BranchId, CustomerDetail, CustomerListOrder, CustomerListSort, CustomerLookups, CustomerRow, CustomerStatusFilter } from './types';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  branchId: BranchId;
}

const CustomerListPanel: React.FC<Props> = ({ branchId }) => {
  const { t } = useTranslation('scenes.customerCenter');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CustomerStatusFilter>('active');
  const [sort, setSort] = useState<CustomerListSort>('name');
  const [order, setOrder] = useState<CustomerListOrder>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const seq = useRef(0);

  const [lookups, setLookups] = useState<CustomerLookups | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formInitial, setFormInitial] = useState<CustomerDetail>(EMPTY_CUSTOMER_DETAIL);
  const [saving, setSaving] = useState(false);
  const [formServerErrors, setFormServerErrors] = useState<Record<string, string>>({});
  const [showImport, setShowImport] = useState(false);
  const [depositsTarget, setDepositsTarget] = useState<CustomerRow | null>(null);

  useDebounce(() => {
    const next = searchInput.trim();
    setPage(1);
    setSearch((prev) => (prev === next ? prev : next));
  }, [searchInput], SEARCH_DEBOUNCE_MS);

  const totalPages = useMemo(() => (total > 0 ? Math.ceil(total / pageSize) : 1), [total, pageSize]);

  const loadList = useCallback(() => {
    if (!branchId) return;
    seq.current += 1;
    const mySeq = seq.current;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setDenied(false);
    fetchCustomerListPage(
      branchId,
      { search, status: statusFilter, sort, order, page, pageSize },
      controller.signal,
    ).then(
      (result) => {
        if (seq.current !== mySeq) return;
        setRows(result.items);
        setTotal(result.total);
        setLoading(false);
      },
      (err: unknown) => {
        if (seq.current !== mySeq) return;
        setRows([]);
        setTotal(0);
        setLoading(false);
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setDenied(true);
          return;
        }
        const message = err instanceof Error ? err.message : t('failedCustomers');
        setError(message);
        toast.error(message);
      },
    );
    return () => controller.abort();
  }, [branchId, search, statusFilter, sort, order, page, pageSize, t]);

  useEffect(() => loadList(), [loadList, refresh]);

  useEffect(() => {
    if (!branchId) return;
    fetchCustomerLookups(branchId).then(setLookups).catch(() => setLookups(null));
  }, [branchId]);

  const openCreate = () => {
    setFormServerErrors({});
    setFormInitial({ ...EMPTY_CUSTOMER_DETAIL, financial: { ...EMPTY_CUSTOMER_DETAIL.financial }, preferences: { ...EMPTY_CUSTOMER_DETAIL.preferences, preferred_transactions: [] } });
    setShowForm(true);
  };

  const openEdit = async (row: CustomerRow) => {
    try {
      const detail = await fetchCustomerDetail(branchId, row.id);
      setFormInitial(detail);
      setShowForm(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('form.loadFailed'));
    }
  };

  const handleSave = (values: CustomerDetail) => {
    setSaving(true);
    setFormServerErrors({});
    const onDone = () => {
      setSaving(false);
      setShowForm(false);
      toast.success(t('form.saved'));
      setRefresh((v) => v + 1);
    };
    const onFail = (err: unknown) => {
      setSaving(false);
      if (err instanceof ApiError) {
        const mapped = mapServerFieldErrors(err.data);
        if (err.status === 409) {
          setFormServerErrors(Object.keys(mapped).length ? mapped : { name: err.message });
          return;
        }
        if (Object.keys(mapped).length) {
          setFormServerErrors(mapped);
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : t('form.saveFailed'));
    };
    if (values.id) {
      updateCustomer(branchId, values.id, values).then(onDone, onFail);
    } else {
      createCustomer(branchId, values).then(onDone, onFail);
    }
  };

  const toggleStatus = (row: CustomerRow) => {
    const nextActive = !row.active;
    const msg = nextActive ? t('confirm.activate', { name: row.name }) : t('confirm.inactivate', { name: row.name });
    if (!window.confirm(msg)) return;
    patchCustomerStatus(branchId, row.id, nextActive)
      .then(() => {
        toast.success(t('form.statusUpdated'));
        setRefresh((v) => v + 1);
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : t('form.saveFailed')));
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      const blob = await exportCustomersFile(branchId, format, search, statusFilter);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = format === 'csv' ? 'customers.csv' : 'customers.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('export.failed'));
    }
  };

  const toggleSort = (column: CustomerListSort) => {
    if (sort === column) setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(column);
      setOrder('asc');
    }
    setPage(1);
  };

  const sortIndicator = (column: CustomerListSort) => (sort === column ? (order === 'asc' ? '▲' : '▼') : '');

  const sortHeader = (column: CustomerListSort, label: string) => (
    <th aria-sort={sort === column ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="customer-center__sort" onClick={() => toggleSort(column)}>
        {label}
        <span className="customer-center__sort-icon" aria-hidden="true">{sortIndicator(column)}</span>
      </button>
    </th>
  );

  return (
    <div className="executive-dashboard" data-testid="customer-list-panel">
      <div className="dashboard-top-part">
        <div className="customer-center__toolbar">
          <Form.Control className="customer-center__search" type="search" placeholder={t('searchPlaceholder')} aria-label={t('searchLabel')} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="customer-search" />
          <Form.Select className="customer-center__filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as CustomerStatusFilter); setPage(1); }} aria-label={t('list.statusFilter')}>
            <option value="all">{t('list.statusAll')}</option>
            <option value="active">{t('list.statusActive')}</option>
            <option value="inactive">{t('list.statusInactive')}</option>
          </Form.Select>
          <div className="customer-center__toolbar-actions">
            <Button variant="outline-primary" onClick={() => setShowImport(true)} data-testid="customer-import-btn">{t('list.import')}</Button>
            <Dropdown align="end">
              <Dropdown.Toggle variant="outline-primary" id="customer-export-dropdown">{t('list.export')}</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleExport('csv')}>CSV</Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('xlsx')}>XLSX</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="primary" onClick={openCreate} data-testid="customer-add-btn">+ {t('list.addCustomer')}</Button>
          </div>
        </div>

        <div className={`page-loader ${loading ? 'visible' : ''}`}><div className="loader" /></div>
        {denied ? <div className="customer-center__error" role="alert">{t('denied')}</div> : null}
        {error ? <div className="customer-center__error" role="alert">{error}</div> : null}

        <div className="table-responsive list-table customer-center__stack-scroll">
          <table className="table table-default table-hover align-middle customer-center__stack-table">
            <thead>
              <tr>
                {sortHeader('customer_id', t('colCustomerId'))}
                {sortHeader('name', t('colCustomer'))}
                {sortHeader('email', t('colEmail'))}
                {sortHeader('phone', t('colPhone'))}
                <th>{t('colCategory')}</th>
                {sortHeader('active', t('colStatus'))}
                <th className="text-end">{t('colLinkedDeposits')}</th>
                <th className="text-end">{t('list.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={String(row.id)} className="h-60">
                  <td data-label={t('colCustomerId')}>{row.customer_id || `#${row.id}`}</td>
                  <td data-label={t('colCustomer')}>
                    <div className="fw-semibold">{row.name}</div>
                    {row.display_name && row.display_name !== row.name ? <small className="text-muted d-block">{row.display_name}</small> : null}
                  </td>
                  <td data-label={t('colEmail')}>{row.email || '—'}</td>
                  <td data-label={t('colPhone')}>{row.phone || '—'}</td>
                  <td data-label={t('colCategory')}>{row.category_name || '—'}</td>
                  <td data-label={t('colStatus')}>
                    {row.active ? (
                      <Badge bg="success">{t('list.activeBadge')}</Badge>
                    ) : (
                      <Badge bg="light" text="dark" className="border">{t('inactive')}</Badge>
                    )}
                  </td>
                  <td className="text-end" data-label={t('colLinkedDeposits')}>{row.linked_deposits_count}</td>
                  <td className="text-end customer-center__row-actions">
                    <Dropdown align="end" className="d-inline-block">
                      <Dropdown.Toggle as={RowMenuToggle} id={`customer-actions-${row.id}`} />
                      <Dropdown.Menu className="shadow-sm border">
                        <Dropdown.Item onClick={() => openEdit(row)}>{t('list.edit')}</Dropdown.Item>
                        <Dropdown.Item onClick={() => toggleStatus(row)}>{row.active ? t('list.inactivate') : t('list.activate')}</Dropdown.Item>
                        <Dropdown.Item onClick={() => setDepositsTarget(row)}>{t('viewDeposits')}</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && !error && !denied ? (
                <tr><td colSpan={8} className="text-center">{search ? t('noMatchSearch') : t('noCustomers')}</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="customer-center__footer">
          <label className="customer-center__page-size">
            <span>{t('list.pageSize')}</span>
            <Form.Select size="sm" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </Form.Select>
          </label>
          <Pagination count={totalPages} page={page} showFirstButton showLastButton onChange={(_e: ChangeEvent<unknown>, value: number) => setPage(value)} size="medium" disabled={!total} />
        </div>
      </div>

      <CustomerFormModal show={showForm} saving={saving} initial={formInitial} lookups={lookups} serverErrors={formServerErrors} onClose={() => setShowForm(false)} onSubmit={handleSave} />
      <ImportCustomersModal show={showImport} branchId={branchId} onClose={() => setShowImport(false)} onImported={() => setRefresh((v) => v + 1)} />
      <CustomerDepositsModal show={Boolean(depositsTarget)} branchId={branchId} customer={depositsTarget} onClose={() => setDepositsTarget(null)} />
    </div>
  );
};

export default CustomerListPanel;
